import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Mail, User, Building2, AlertCircle, CheckCircle2, Gift, Ticket } from "lucide-react";
import AuthLayout from "@/components/auth/AuthLayout";
import PasswordField from "@/components/auth/PasswordField";
import SocialButtons from "@/components/auth/SocialButtons";
import OtpVerify from "@/components/auth/OtpVerify";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import Container from "@/components/ui/Container";
import Logo from "@/components/common/Logo";
import ThemeToggle from "@/components/ui/ThemeToggle";
import PlanCard from "@/components/billing/PlanCard";
import { useAuth, homeForRole } from "@/context/AuthContext";
import { validateFullName, validateCompanyName, validateEmail } from "@/lib/validation";
import { createOrder, verifyPayment, validateCoupon } from "@/api/billing";
import { loadRazorpay } from "@/lib/razorpay";
import { usePlans } from "@/hooks/usePlans";
import { cn } from "@/lib/cn";

// Which plans the signup picker offers — decided by the CATALOG, not by a fixed
// list of ids. It used to be ["free_trial", "starter", "growth"], so a plan an
// administrator created could never appear here however it was configured.
//
// The rule it replaces is preserved: a plan with no self-serve price (null =
// "talk to sales") is still excluded, because a checkout button for it could
// only ever fail server-side. What decides the rest is the plan's own metadata —
// `is_trial` for the free-trial card, `is_signup_pack` for the paid ones — which
// is exactly what the backend's own signup validation now reads.
function signupPickerPlans(plans) {
  return (plans || [])
    .filter((p) => p
      && p.active !== false
      && p.price_inr !== null && p.price_inr !== undefined   // no contact-sales card
      && (p.is_trial || p.is_signup_pack))
    // The catalog's own order, so the picker matches the pricing page.
    .sort((a, b) => (a.sort_order ?? 100) - (b.sort_order ?? 100)
      || String(a.name || "").localeCompare(String(b.name || ""))
      || String(a.id).localeCompare(String(b.id)));
}

export default function Signup() {
  const navigate = useNavigate();
  // Live catalog, so the price and allowance shown on the plan picker are the
  // ones the customer is actually charged for and held to.
  const { plans } = usePlans();
  const { register, verifyEmailOtp, resendSignupOtp } = useAuth();
  // Referral code from a ?ref=<code> signup link (#1), threaded through both steps.
  const params = new URLSearchParams(window.location.search);
  const ref = params.get("ref") || null;
  // A pack chosen on the landing page (?plan=starter|growth) still travels
  // through registration — it no longer skips the picker, but it does highlight
  // that card as "Recommended" so the choice they already made is honoured
  // rather than thrown away.
  const planParam = params.get("plan") || null;

  // "form" → details + trigger the code · "verify" → OTP · "choose-plan" → pick
  // Free Trial / Starter / Growth before entering the app.
  const [step, setStep] = useState("form");
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    password: "",
    agree: false,
  });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);

  // Verify-step state
  const [code, setCode] = useState("");
  const [otpError, setOtpError] = useState("");
  // "idle" | "verifying" | "success" | "error" — what the verify panel animates
  // to. Derived from the request, never from a timer, so the tick only ever
  // appears after the server has actually accepted the code.
  const [otpStatus, setOtpStatus] = useState("idle");
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [resentNote, setResentNote] = useState("");

  // Plan-picker step state. The account already exists and the caller is
  // already signed in by the time this renders (verifyEmailOtp persists the
  // session token), so every write below runs authenticated — there is no
  // client-trusted "I already paid" flag anywhere in this flow; the server
  // verifies the Razorpay signature itself in /billing/verify-payment.
  const [role, setRole] = useState("admin");
  const [highlightPlanId, setHighlightPlanId] = useState(null);
  // Which plan card is mid-checkout — not a bare boolean, so clicking Starter
  // doesn't also grey out and spin the Growth card next to it.
  const [busyPlanId, setBusyPlanId] = useState(null);
  const [checkoutError, setCheckoutError] = useState("");
  const [coupon, setCoupon] = useState("");
  // What the SERVER said about the entered code, per plan:
  //   { checking, message, ok, byPlan: { [planId]: { code, discount, final, currency } } }
  // The code has always travelled to create-order/verify-payment from here, but
  // the field was an unlabeled 160px box with no action and no response — a
  // buyer could type a dead code, pay full price, and never be told the coupon
  // had not applied. Nothing here decides the charge: every entry in `byPlan` is
  // a POST /billing/validate-coupon response, and the real discount is computed
  // again server-side when the order is created.
  const [couponState, setCouponState] = useState(null);
  // Same toggle as dashboard/Billing.jsx (#89/#90) — the signup picker used
  // PlanCard too but never gave it anything but the "monthly" default, so a
  // signing-up customer had no way to see or choose the yearly price (#84).
  const [billingPeriod, setBillingPeriod] = useState("monthly");

  const update = (key) => (e) => {
    const value = key === "agree" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
    setFormError("");
  };

  // A coupon's discount is period-specific (a percentage off a yearly price is a
  // different figure, and a fixed amount may exceed a monthly one), so an
  // already-applied code has to be re-checked when the toggle moves — otherwise
  // the card kept quoting the monthly saving beside a yearly price.
  useEffect(() => {
    if (!couponState || !couponState.ok) return;
    checkCoupon(coupon, billingPeriod, paidPickerPlans);
    // `couponState` is deliberately absent: this reacts to the PERIOD changing,
    // and depending on the thing it writes would re-run itself forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingPeriod]);

  // Resend cooldown ticker.
  const timerRef = useRef(null);
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    timerRef.current = setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(timerRef.current);
  }, [cooldown]);

  // Step 1 — validate + request the verification code.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    const next = {};
    // A bare length check let "123" and "@#$%" through and the API had no name
    // rule at all, so an account really was created under them.
    const nameError = validateFullName(form.name);
    if (nameError) next.name = nameError;
    const companyError = validateCompanyName(form.company);
    if (companyError) next.company = companyError;
    const emailError = validateEmail(form.email);
    if (emailError) next.email = emailError;
    if (form.password.length < 8) next.password = "Use at least 8 characters.";
    if (!form.agree) next.agree = "Please accept the terms.";
    setErrors(next);
    if (Object.keys(next).length) return;

    // Calls POST /auth/register → emails a code (verify-then-register). Demo
    // fallback signs in directly when the backend is offline.
    setLoading(true);
    try {
      const res = await register(form.email.trim(), form.password, {
        name: form.name.trim(),
        companyName: form.company.trim(),
        ref,
        plan: planParam,
      });
      // Verification disabled (or offline demo) → account is ready. Even here
      // the reader still gets a chance to choose a plan, rather than being
      // dropped straight into the app on whatever the server defaulted to.
      if (res?.verified) {
        setRole(res?.user?.role || "admin");
        setHighlightPlanId(planParam);
        setStep("choose-plan");
        return;
      }
      // Verification required → move to the OTP step.
      setCode("");
      setOtpError("");
      setResentNote("");
      setCooldown(res?.resendIn || 60);
      setStep("verify");
    } catch (err) {
      setFormError(err.message || "Sign up failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Step 2 — confirm the code → account is created → sign in → choose a plan.
  const submitCode = async (value) => {
    const otp = (value ?? code).replace(/\D/g, "");
    if (otp.length !== 6 || verifying) return;
    setVerifying(true);
    setOtpError("");
    setResentNote("");
    setOtpStatus("verifying");
    try {
      const res = await verifyEmailOtp(form.email.trim(), otp, ref);
      setOtpStatus("success");
      // Let the tick land before leaving — cutting the success animation off
      // mid-draw reads as a glitch rather than a confirmation.
      await new Promise((r) => { setTimeout(r, 900); });
      setRole(res?.user?.role || "admin");
      // `res.plan` is only set when a pack was picked on the pricing page
      // before signup — used purely to highlight that card, never to skip
      // the picker the way it used to.
      setHighlightPlanId(res?.plan || null);
      setCheckoutError("");
      setStep("choose-plan");
    } catch (err) {
      // The server's wording carries the useful part ("2 attempts left"), so
      // pass it through rather than replacing it with something generic.
      setOtpError(err.message || "Verification failed. Please try again.");
      setOtpStatus("error");
      setCode("");
    } finally {
      setVerifying(false);
    }
  };

  const enterApp = () => navigate(homeForRole(role || "admin"), { replace: true });

  // Check the entered code against every PAID plan on the picker and keep what
  // the server said about each.
  //
  // Per plan, not once: a coupon may be restricted to one plan (applies_to_plans),
  // and the discount differs by plan and by billing period, so a single "is this
  // code real" answer could not say which card it changes. The picker holds two
  // or three paid plans, so this is two or three calls behind one button.
  //
  // A free/trial card is skipped — validate-coupon would be answering about a
  // zero charge, and a coupon can never apply to one (see the Coupons admin
  // page and admin_coupons._is_coupon_eligible).
  // Hoisted out of the plan-picker branch below: the coupon effect has to depend
  // on these, and a hook cannot live inside a conditional return.
  const pickerPlans = useMemo(() => signupPickerPlans(plans), [plans]);
  // Only the cards a coupon could ever change. A free/trial card has no charge
  // to discount, so it is not worth a validate call and must not be reported as
  // "the coupon doesn't apply here".
  const paidPickerPlans = useMemo(
    () => pickerPlans.filter((p) => p.price_inr != null && Number(p.price_inr) > 0),
    [pickerPlans]);

  const checkCoupon = useCallback(async (rawCode, period, paidPlans) => {
    const code = (rawCode || "").trim().toUpperCase();
    if (!code) { setCouponState(null); return; }
    if (!paidPlans.length) return;                 // catalog hasn't loaded yet
    setCouponState({ checking: true, byPlan: {} });
    const results = await Promise.all(paidPlans.map(async (plan) => {
      try {
        const res = await validateCoupon(plan.id, code, period);
        return { plan, res, error: null };
      } catch (e) {
        // A rejection is an ANSWER, not a failure — "doesn't apply to this plan"
        // is exactly what we need to know for the other cards.
        return { plan, res: null, error: e };
      }
    }));
    const byPlan = {};
    results.forEach(({ plan, res }) => {
      if (res && Number(res.discount) > 0) {
        byPlan[plan.id] = {
          code,
          discount: Number(res.discount),
          final: Number(res.final_price),
          currency: res.currency,
        };
      }
    });
    const applied = Object.keys(byPlan);
    if (applied.length) {
      const names = results
        .filter(({ plan }) => byPlan[plan.id])
        .map(({ plan }) => plan.name || plan.id);
      setCouponState({
        checking: false, ok: true, byPlan,
        message: applied.length === paidPlans.length
          ? `"${code}" applied — the discounted price is shown on each plan.`
          : `"${code}" applies to ${names.join(" and ")} — the discounted price is shown on that plan.`,
      });
      return;
    }
    // Nothing applied. Surface the server's own reason (expired / inactive /
    // limit reached / wrong plan) rather than a generic "invalid", so the buyer
    // knows whether to fix the code or stop trying.
    const firstError = results.find((r) => r.error);
    setCouponState({
      checking: false, ok: false, byPlan: {},
      message: (firstError && firstError.error && firstError.error.message)
        || `"${code}" gives no discount on these plans.`,
    });
  }, []);

  // Free → straight in, nothing to charge (the server rejects a ₹0 order
  // outright, so this branch is load-bearing, not cosmetic). Custom/Enterprise
  // is never selectable here — signupPickerPlans() drops any null-priced plan,
  // and PlanCard's own
  // button only calls onSelect for a plan with a real, non-null price — but the
  // guard stays as a second layer rather than trusting the one upstream of it.
  // Starter/Growth → Razorpay, then the SAME server-verified path Billing uses.
  async function handleSelectPlan(plan) {
    if (busyPlanId) return; // a checkout is already in flight
    const isCustom = plan.price_inr === null || plan.price_inr === undefined;
    if (isCustom) return;
    const isFree = Number(plan.price_inr) === 0;
    setCheckoutError("");

    if (isFree) {
      enterApp();
      return;
    }

    setBusyPlanId(plan.id);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Couldn't load the payment gateway. Check your connection and retry.");
      const order = await createOrder(plan.id, coupon.trim() || null, billingPeriod);
      await new Promise((resolve) => {
        const rzp = new window.Razorpay({
          key: order.key_id,
          subscription_id: order.subscription_id,
          name: "AI-QA-Engine",
          description: plan.name,
          // No fabricated phone number: "9999999999" used to be prefilled into
          // the customer-facing Razorpay checkout. Let Razorpay collect it.
          prefill: { name: order.user_name, email: order.user_email },
          theme: { color: "#7c3aed" },
          handler: (response) => {
            // The signature is verified SERVER-SIDE here — this call is the
            // trust boundary, not the Razorpay handler firing. A tampered or
            // replayed response fails verifyPayment and never activates a plan.
            verifyPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: plan.id,
              coupon_code: coupon.trim() || null,
              billing_period: billingPeriod,
            })
              .then((result) => {
                if (result?.success) enterApp();
                else setCheckoutError("We couldn't verify the payment. Please try again.");
                resolve();
              })
              .catch((err) => {
                setCheckoutError(err.message || "Payment verification failed. Please try again.");
                resolve();
              });
          },
          modal: { ondismiss: () => resolve() },
        });
        rzp.on("payment.failed", (response) => {
          setCheckoutError(response.error?.description || "Payment failed. Retry, or start with the Free Trial instead.");
          resolve();
        });
        rzp.open();
      });
    } catch (e) {
      setCheckoutError(e.message || "Payment failed. Please try again.");
    } finally {
      setBusyPlanId(null);
    }
  }

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setOtpError("");
    setResentNote("");
    setOtpStatus("idle");
    try {
      const res = await resendSignupOtp(form.email.trim());
      setCooldown(res?.resendIn || 60);
      setResentNote("A new code is on its way.");
    } catch (err) {
      setOtpError(err.message || "Couldn't resend the code. Please try again.");
    } finally {
      setResending(false);
    }
  };

  // ---- Plan-picker step (every signup lands here) ----------------------------
  if (step === "choose-plan") {

    return (
      <div className="min-h-screen bg-slate-50 dark:bg-ink-950">
        <header className="border-b border-slate-200/70 bg-white/80 backdrop-blur-xl dark:border-white/10 dark:bg-ink-950/80">
          <Container className="flex h-16 items-center justify-between">
            <Logo />
            <ThemeToggle />
          </Container>
        </header>

        <main>
          <Container className="max-w-5xl py-12 sm:py-16">
            <div className="mx-auto max-w-2xl text-center">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-600 dark:text-emerald-300">
                <CheckCircle2 size={13} /> Account created
              </span>
              <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
                Choose your plan
              </h1>
              <p className="mt-3 text-slate-600 dark:text-slate-300">
                Pick how you&apos;d like to get started. You can change or cancel this anytime from Billing.
              </p>
            </div>

            {checkoutError && (
              <div
                role="alert"
                className="mx-auto mt-6 flex max-w-2xl items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300"
              >
                <AlertCircle size={18} className="mt-0.5 shrink-0" />
                <span>{checkoutError}</span>
              </div>
            )}

            {pickerPlans.length === 0 ? (
              <div className="mt-12 flex justify-center">
                <Loader2 size={26} className="animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                {/* Same Monthly/Yearly toggle as dashboard/Billing.jsx (#89/#90)
                    — PlanCard already supports billingPeriod, this page just
                    never gave the customer a way to choose it (#84). */}
                <div className="mt-8 flex justify-center">
                  <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1 dark:border-white/10 dark:bg-white/5">
                    <button
                      type="button"
                      onClick={() => setBillingPeriod("monthly")}
                      aria-pressed={billingPeriod === "monthly"}
                      className={cn(
                        "rounded-full px-4 py-1.5 text-sm font-semibold transition-colors",
                        billingPeriod === "monthly"
                          ? "bg-brand-600 text-white shadow-soft"
                          : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                      )}
                    >
                      Monthly
                    </button>
                    <button
                      type="button"
                      onClick={() => setBillingPeriod("yearly")}
                      aria-pressed={billingPeriod === "yearly"}
                      className={cn(
                        "flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors",
                        billingPeriod === "yearly"
                          ? "bg-brand-600 text-white shadow-soft"
                          : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                      )}
                    >
                      Yearly
                      <span className={cn(
                        "rounded-full px-1.5 py-0.5 text-[0.625rem] font-bold",
                        billingPeriod === "yearly" ? "bg-white/20 text-white" : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300"
                      )}>
                        -20%
                      </span>
                    </button>
                  </div>
                </div>
                {/* Coupon entry, at the point the plan is actually bought.
                    This was an unlabeled box floated into the corner with no
                    button and no response of any kind: a buyer could enter a
                    dead code, complete a full-price checkout and never learn it
                    had not applied. Apply asks the SERVER, and what comes back
                    is what the cards below quote. */}
                {paidPickerPlans.length > 0 && (
                  <div className="mx-auto mt-6 max-w-md rounded-xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/5">
                    <label htmlFor="signup-coupon" className="flex items-center gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                      <Ticket size={15} className="text-slate-400" /> Have a coupon code?
                    </label>
                    <div className="mt-2 flex gap-2">
                      <input
                        id="signup-coupon"
                        value={coupon}
                        maxLength={40}
                        onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setCouponState(null); }}
                        onKeyDown={(e) => {
                          if (e.key !== "Enter") return;
                          e.preventDefault();                 // never submit the page
                          checkCoupon(coupon, billingPeriod, paidPickerPlans);
                        }}
                        placeholder="Coupon code"
                        aria-describedby="signup-coupon-status"
                        aria-invalid={couponState && couponState.ok === false ? "true" : undefined}
                        className={cn(
                          "h-10 min-w-0 flex-1 rounded-lg border bg-white px-3 font-mono text-sm uppercase placeholder:font-sans placeholder:normal-case dark:bg-white/5 dark:text-slate-200",
                          couponState && couponState.ok === false
                            ? "border-rose-300 dark:border-rose-500/40"
                            : "border-slate-200 dark:border-white/10"
                        )}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-10 shrink-0"
                        disabled={!coupon.trim() || (couponState && couponState.checking)}
                        onClick={() => checkCoupon(coupon, billingPeriod, paidPickerPlans)}
                      >
                        {couponState && couponState.checking
                          ? <Loader2 size={14} className="animate-spin" />
                          : "Apply"}
                      </Button>
                    </div>
                    <p id="signup-coupon-status" role="status" className="mt-2 text-xs">
                      {couponState && couponState.checking ? (
                        <span className="text-slate-400">Checking…</span>
                      ) : couponState && couponState.message ? (
                        <span className={couponState.ok
                          ? "font-medium text-emerald-600 dark:text-emerald-400"
                          : "font-medium text-rose-600 dark:text-rose-400"}>
                          {couponState.message}
                        </span>
                      ) : coupon.trim() ? (
                        <span className="text-slate-400">Press Apply to check this code.</span>
                      ) : (
                        <span className="text-slate-400">Optional — the discount is applied when you pay.</span>
                      )}
                    </p>
                  </div>
                )}
                <div className="mt-6 grid gap-5 sm:grid-cols-3">
                  {pickerPlans.map((plan) => (
                    <PlanCard
                      key={plan.id}
                      plan={plan}
                      onSelect={handleSelectPlan}
                      busyPlanId={busyPlanId}
                      highlight={highlightPlanId === plan.id}
                      billingPeriod={billingPeriod}
                      // Straight from validate-coupon for THIS plan, or nothing.
                      couponPreview={(couponState && couponState.byPlan && couponState.byPlan[plan.id]) || null}
                    />
                  ))}
                </div>
              </>
            )}

            <p className="mt-8 text-center text-xs text-slate-400">
              Secure payment via Razorpay. Free Trial needs no card.
            </p>
          </Container>
        </main>
      </div>
    );
  }

  // ---- Verify step -----------------------------------------------------------
  if (step === "verify") {
    return (
      // No layout title here: the panel owns its own heading so it can crossfade
      // to "Verified" without a second, contradictory heading above it.
      <AuthLayout>
        <OtpVerify
          length={6}
          email={form.email.trim()}
          status={otpStatus}
          error={otpError}
          note={!otpError ? resentNote : ""}
          resendIn={cooldown}
          resending={resending}
          onComplete={(value) => { setCode(value); submitCode(value); }}
          onResend={handleResend}
          onBack={() => {
            setStep("form");
            setOtpError("");
            setResentNote("");
            setOtpStatus("idle");
            setCode("");
          }}
        />
      </AuthLayout>
    );
  }

  // ---- Form step -------------------------------------------------------------
  return (
    <AuthLayout
      title="Create your account"
      subtitle="Start testing in plain English — free, no card needed."
    >
      <SocialButtons />

      <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
        <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
        or sign up with email
        <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {/* States only that a referral code is present on the link — never that
            it was accepted. A mistyped or expired code is silently ignored
            server-side (record_referral) so signup is never blocked by it, and
            this banner has no way to know that ahead of the actual signup call. */}
        {ref && (
          <div className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-sm text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
            <Gift size={18} className="mt-0.5 shrink-0 text-brand-500" />
            <span>Signing up with referral code <span className="font-mono font-semibold">{ref}</span>.</span>
          </div>
        )}
        {formError && (
          <div
            role="alert"
            className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300"
          >
            <AlertCircle size={18} className="mt-0.5 shrink-0" />
            <span>{formError}</span>
          </div>
        )}
        <Input
          id="name"
          label="Full name"
          icon={User}
          autoComplete="name"
          placeholder="Your full name"
          value={form.name}
          onChange={update("name")}
          error={errors.name}
        />
        <Input
          id="company"
          label="Company name"
          icon={Building2}
          placeholder="Acme Inc."
          value={form.company}
          onChange={update("company")}
          error={errors.company}
        />
        <Input
          id="email"
          type="email"
          label="Work email"
          icon={Mail}
          autoComplete="email"
          placeholder="you@company.com"
          value={form.email}
          onChange={update("email")}
          error={errors.email}
        />
        <PasswordField
          value={form.password}
          onChange={update("password")}
          error={errors.password}
          autoComplete="new-password"
          showStrength
          placeholder="Create a strong password"
        />

        <label className="flex items-start gap-2.5 text-sm text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={form.agree}
            onChange={update("agree")}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          <span>
            I agree to the{" "}
            <a href="/terms" target="_blank" rel="noreferrer" className="font-medium text-brand-600 dark:text-brand-400">
              Terms
            </a>{" "}
            and{" "}
            <a href="/privacy" target="_blank" rel="noreferrer" className="font-medium text-brand-600 dark:text-brand-400">
              Privacy Policy
            </a>
            .
          </span>
        </label>
        {errors.agree && (
          <span className="block text-xs font-medium text-rose-500">
            {errors.agree}
          </span>
        )}

        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin" /> Sending code…
            </>
          ) : (
            <>
              Create account <ArrowRight size={18} />
            </>
          )}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
        Already have an account?{" "}
        <Link
          to="/login"
          className="font-semibold text-brand-600 hover:text-brand-500 dark:text-brand-400"
        >
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
